import { z } from 'zod';
import { COPACK_FILTRES } from '../domain/copacking-roles.js';
/**
 * Écarts quotidiens et leur date d'apparition (spec cycle, temps 2).
 *
 * Un écart est le **cumul** depuis l'ancrage du 01/01/2026 de la différence
 * entre mouvements physiques et mouvements déclarés. Il naît le jour où ce
 * cumul quitte zéro et se solde le jour où il y revient ; entre les deux, sa
 * date d'apparition ne bouge pas, quelle que soit l'évolution du montant.
 */
/**
 * Maille d'un écart suivi. Les familles validées portent l'écart de leurs
 * membres ; un article sans famille reste suivi pour lui-même.
 */
export const DISCREPANCY_SCOPES = ['ARTICLE', 'FAMILLE'];
export const DISCREPANCY_SCOPE_LABELS = {
    ARTICLE: 'Article',
    FAMILLE: "Famille d'équivalence",
};
export const DISCREPANCY_STATUSES = ['ACTIF', 'SOLDE'];
export const DISCREPANCY_STATUS_LABELS = {
    ACTIF: 'Actif',
    SOLDE: 'Soldé',
};
/**
 * Seuil d'ancienneté du KPI d'instruction : au-delà de J+5 entre détection et
 * solde, l'écart est mis en évidence.
 */
export const ANCIENNETE_ALERTE_JOURS = 5;
export const listDiscrepanciesQuerySchema = z.object({
    /**
     * Maille de lecture. `FAMILLE` agrège les membres des familles validées et
     * laisse les articles orphelins en lignes article : rien ne disparaît.
     */
    maille: z.enum(DISCREPANCY_SCOPES).default('ARTICLE'),
    /** Recherche sur le code, le libellé ou un code membre. */
    search: z.string().trim().max(120).optional(),
    /**
     * Instruire plusieurs articles liés ensemble (demande atelier) : la liste
     * restreint l'écran à ces seules clés.
     */
    cles: z
        .union([z.string(), z.array(z.string())])
        .transform((valeur) => (Array.isArray(valeur) ? valeur : valeur.split(','))
        .map((cle) => cle.trim())
        .filter((cle) => cle.length > 0))
        .default([]),
    inclureSoldes: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    /** Filtre par rôle de copacking (demande Léonie, 11/09/2026). */
    copack: z.enum(COPACK_FILTRES).optional(),
    /** N'afficher que les écarts portant au moins un commentaire. */
    avecCommentaires: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    /** Filtrer sur le type du **dernier** commentaire. */
    commentaireType: z.string().trim().max(60).optional(),
    /** N'afficher que les écarts que le dernier import a fait bouger. */
    evoluesAuDernierImport: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    inclureHorsControle: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
    /** Afficher les écarts entièrement couverts par une validation (règle 14). */
    inclureValides: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(false),
});
// --- Validations d'écart (règle 14) -----------------------------------------
export const VALIDATION_TYPES = ['TEMPORAIRE', 'DEFINITIVE'];
export const VALIDATION_TYPE_LABELS = {
    TEMPORAIRE: 'Temporaire',
    DEFINITIVE: 'Définitive',
};
export const VALIDATION_TYPE_AIDES = {
    TEMPORAIRE: "L'écart est expliqué mais pas résorbé : il sort de la vue quotidienne à hauteur de la quantité validée, et ressort si l'écart s'aggrave.",
    DEFINITIVE: "L'écart est revenu à zéro après régularisation : la validation le constate et solde les validations temporaires qu'elle couvre.",
};
export const createValidationSchema = z.object({
    type: z.enum(VALIDATION_TYPES),
    quantite: z
        .string()
        .trim()
        .regex(/^-?\d+(\.\d{1,3})?$/, 'Quantité attendue en UVC, décimal exact'),
    /** Obligatoire : une validation sans explication ne vaut rien (règle 14). */
    commentaire: z
        .string({ required_error: 'Le commentaire est obligatoire' })
        .trim()
        .min(10, 'Le commentaire doit expliquer l’écart (10 caractères au minimum)')
        .max(2000),
    motif: z.string().trim().max(20).optional(),
    mouvements: z
        .array(z.object({
        jour: z.string(),
        quantite: z.string(),
        refMvt: z.string().nullable(),
        motif: z.string(),
    }))
        .optional(),
});
export const cancelValidationSchema = z.object({
    motifAnnulation: z
        .string({ required_error: "Le motif d'annulation est obligatoire" })
        .trim()
        .min(10, "Le motif d'annulation doit être explicite (10 caractères au minimum)")
        .max(2000),
});
export const CouvertureRematchValues = ['EXACTE', 'PARTIELLE', 'ABSENTE'];
export const COUVERTURE_REMATCH_LABELS = {
    EXACTE: 'Couverture exacte',
    PARTIELLE: 'Couverture partielle',
    ABSENTE: 'Aucun ODE rapproché',
};
/** Période du récapitulatif : par défaut l'année en cours. */
export const closingQuerySchema = z.object({
    du: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    au: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date attendue au format AAAA-MM-JJ')
        .optional(),
    maille: z.enum(DISCREPANCY_SCOPES).default('ARTICLE'),
});
/** Période de l'historique des validations. Par défaut, l'année en cours. */
export const historiqueQuerySchema = closingQuerySchema.omit({ maille: true }).extend({
    /** Inclure les validations annulées : elles restent tracées (règle 14). */
    inclureAnnulees: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .default(true),
});
//# sourceMappingURL=discrepancy.js.map